import React, { forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { CentricTypography } from '@core/component/CentricTypography'
import { makeStyles } from '@material-ui/core'
import QtyNumberInput from '@api/order/components/CellTemplates/components/QtyNumberInput'
import IAReadOnlyField from './IAReadOnlyField'
import { useObserver } from 'mobx-react-lite'
import classNames from 'classnames'
import formatNumberByLocale from '@utils/format-number-by-locale'
import { stores } from '@stores'
import EnhancedQtyCheckbox from '../EnhancedCellTemplates/EnhancedQuantityCell/EnhancedQtyCheckbox'

export interface IADoorLevelTotalQuantityProps {
  doorProduct: any
  IADoorProduct: any
  disabled: boolean
  isExclusionDoor: boolean
  highlightDoors: boolean
  isBooked: boolean
  onChange: any
  onKeyDown: any
  autoFocus: any
  onFocus: any
  onMouseDown: any
  onBlur: any
}

const useStyles = makeStyles(theme => ({
  containerRoot: {
    width: '100%'
  },
  bookedStatusCheckbox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    height: 65,
    borderBottom: '1px solid #eeeeee'
  },
  doorQuantityCommon: {
    width: '100%',
    height: 105,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '10px 10px 10px 10px',
    borderBottom: '1px solid #EEEEEE',
    background: theme.palette.background.default
  },
  doorQuantity: {
    background: 'transparent'
  },
  doorContainer: {
    display: 'flex',
    alignItems: 'center',
    height: 35,
    flexDirection: 'column'
  },
  IADoorLevelQtyInput: {
    width: 100,
    height: 20
  }
}))

/**
 * IA door cell top subcomponent with book/unbook checkbox, Door Quantity, and IA Door Quantity input
 */
const IADoorLevelTotalQuantity = forwardRef<
  HTMLInputElement,
  IADoorLevelTotalQuantityProps
>(
  (
    {
      disabled,
      isExclusionDoor,
      doorProduct,
      IADoorProduct,
      highlightDoors,
      isBooked,
      onChange,
      onKeyDown,
      autoFocus,
      onFocus,
      onMouseDown,
      onBlur
    },
    ref
  ) =>
    useObserver(function useHook() {
      const { t } = useTranslation()
      const classes = useStyles()
      const {
        productConfiguration: { featureFlags }
      } = stores
      const totalDoorQuantity = doorProduct?.doorOrderProductDoorQuantity || 0
      const IADoorQuantity = IADoorProduct?.deliveryDoorQuantity || 0
      const highlightIAQuantity =
        featureFlags?.highlightQuantityDiscrepancies && totalDoorQuantity < IADoorQuantity

      return (
        <div className={classes.containerRoot}>
          <div className={classes.bookedStatusCheckbox}>
            <EnhancedQtyCheckbox
              disabled={true}
              onChange={onChange}
              isBooked={isBooked}
              isBookOpPending={false} // TODO: handle pending state
              isHardExcluded={isExclusionDoor}
            />
          </div>
          <IAReadOnlyField
            label={t('orderTable.cnfActualQty', 'Door Quantity')}
            value={formatNumberByLocale(totalDoorQuantity)}
          />
          <div
            className={classNames(classes.doorQuantityCommon, {
              [classes.doorQuantity]: highlightDoors
            })}
          >
            <div className={classes.doorContainer}>
              <CentricTypography variant='body2'>
                {t('order.initialAllocation.cnfIAActualQty', 'IA Door Quantity')}
              </CentricTypography>
              <QtyNumberInput
                onChange={onChange}
                value={IADoorQuantity}
                disabled={disabled}
                ref={ref}
                onKeyDown={e => onKeyDown(e)}
                autoFocus={autoFocus}
                onFocus={onFocus}
                onMouseDown={onMouseDown}
                onBlur={onBlur}
                className={classes.IADoorLevelQtyInput}
                highlightInput={highlightIAQuantity}
              />
            </div>
          </div>
        </div>
      )
    })
)
export default IADoorLevelTotalQuantity
